import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportEquipmentCostDetails from "hooks/organization/reports/types/useGetReportEquipmentCostDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableEquipmentCostDetails(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportEquipmentCostDetails, { data, loading, error }] =
    useGetReportEquipmentCostDetails(false);

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        page,
        first,
      };
      getReportEquipmentCostDetails({ variables });
    },
    [startDate, endDate, getReportEquipmentCostDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportEquipmentCostDetails?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Job ID",
        accessor: "jobId",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Equipment Id",
        accessor: "equipmentId",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Equipment Name",
        accessor: "equipmentName",
        Cell: ({ value }) => capitalCase(value ?? ""),
        disableSortBy: true,
      },
      {
        Header: "Equipment Hours",
        accessor: "equipmentHours",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Equipment Cost",
        accessor: "equipmentCost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportEquipmentCostDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportEquipmentCostDetails: paginate,
  } as const;
}
